import { gsap } from "gsap";
import { CustomEase } from "gsap/CustomEase";
import { config } from "../../../config";
gsap.registerPlugin(CustomEase);

// クリップ形状。中央で高さ0・幅100%に潰れた直線（ハンドルも同じY上にあるので曲がらない）から、
// 上下の辺を円弧で削り込んだ最終形へ、対応する点ごとに座標補間してモーフする。
// PC/SPでアスペクト比が異なる（.p-top-mv-imagesのaspect-ratioが1033/550→375/503に切り替わる）ため、
// 形状パラメータ（width/height/radius/depth）ごとビューポートで切り替える。

type Shape = {
  width: number;
  height: number;
  radius: number;
  depth: number;
};

// PC: 幅1033のとき直径2968の正円（半径1484）で、上下の辺それぞれ中央93px内側まで削り込む。
const PC_SHAPE: Shape = { width: 1033, height: 550, radius: 1484, depth: 93 };

// SP: 幅376×高さ504、上下の辺それぞれ中央40.3px内側まで削り込む
// （半径は sagitta の公式 R=(a²+s²)/(2s) から算出、a=188, s=40.3 → 約458.66）。
// 元のデザインパス（M375.22 503.05V0C318.01 25.89 ...）は下辺右側のハンドルに
// 誤り（左右非対称）があったため、そのままは使わず同じ数式で対称に再構築している。
const SP_SHAPE: Shape = { width: 376, height: 504, radius: 458.6612, depth: 40.3 };

type Seg = { type: "M"; x: number; y: number } | { type: "H"; x: number } | { type: "V"; y: number } | { type: "C"; x1: number; y1: number; x2: number; y2: number; x: number; y: number } | { type: "Z" };

// 円弧をキュービックベジェ1本で近似する（corner→apex間の弧を、接線方向のハンドル長 k で近似）
const buildArc = (shape: Shape): { c1: [number, number]; c2: [number, number]; apex: [number, number] } => {
  const { radius, depth } = shape;
  const halfWidth = shape.width / 2;
  const cyTop = depth - radius; // 上辺用の円の中心Y（画面外の遥か上）
  const thetaApex = Math.atan2(radius, 0); // 頂点は中心の真下 = 90°
  const thetaCorner = Math.atan2(0 - cyTop, 0 - halfWidth); // コーナー(x=0,y=0)方向の角度
  const dTheta = thetaCorner - thetaApex;
  const k = (radius * 4) / 3 * Math.tan(dTheta / 4);

  const point = (theta: number): [number, number] => [
    halfWidth + radius * Math.cos(theta),
    cyTop + radius * Math.sin(theta),
  ];
  const tangent = (theta: number): [number, number] => [Math.sin(theta), -Math.cos(theta)];

  const p0 = point(thetaCorner);
  const p1 = point(thetaApex);
  const t0 = tangent(thetaCorner);
  const t1 = tangent(thetaApex);

  return {
    c1: [p0[0] + k * t0[0], p0[1] + k * t0[1]],
    c2: [p1[0] - k * t1[0], p1[1] - k * t1[1]],
    apex: p1,
  };
};

// 最終形状：上記の円弧を左右・上下に鏡像展開して組み立てる（コーナーは厳密に 0 / width / height にスナップ）
const buildEndSegs = (shape: Shape): Seg[] => {
  const { width, height } = shape;
  const halfWidth = width / 2;
  const arc = buildArc(shape);
  const mirrorX = (x: number): number => width - x;
  const mirrorY = (y: number): number => height - y;

  return [
    { type: "M", x: width, y: height },
    // bottom-right-half（M → 頂点）: top-left-half を上下反転
    { type: "C", x1: mirrorX(arc.c1[0]), y1: mirrorY(arc.c1[1]), x2: mirrorX(arc.c2[0]), y2: mirrorY(arc.c2[1]), x: halfWidth, y: mirrorY(arc.apex[1]) },
    // bottom-left-half（頂点 → 左下コーナー）: top-right-half を上下反転
    { type: "C", x1: arc.c2[0], y1: mirrorY(arc.c2[1]), x2: arc.c1[0], y2: mirrorY(arc.c1[1]), x: 0, y: height },
    { type: "V", y: 0 },
    // top-left-half（左上コーナー → 頂点）
    { type: "C", x1: arc.c1[0], y1: arc.c1[1], x2: arc.c2[0], y2: arc.c2[1], x: halfWidth, y: arc.apex[1] },
    // top-right-half（頂点 → 右上コーナー）: 左半分をX反転
    { type: "C", x1: mirrorX(arc.c2[0]), y1: arc.c2[1], x2: mirrorX(arc.c1[0]), y2: arc.c1[1], x: width, y: 0 },
    { type: "V", y: height },
    { type: "Z" },
  ];
};

// 開始形状：同じコマンド構造のまま、Y座標（制御ハンドル含む）をすべて中央に揃えた「高さ0・幅100%」の直線
const buildStartSegs = (shape: Shape, endSegs: Seg[]): Seg[] => {
  const centerY = shape.height / 2;
  return endSegs.map((seg) => {
    switch (seg.type) {
      case "M":
      case "V":
        return { ...seg, y: centerY };
      case "C":
        return { ...seg, y1: centerY, y2: centerY, y: centerY };
      default:
        return seg;
    }
  });
};

const lerp = (a: number, b: number, t: number): number => a + (b - a) * t;

// ease-in-sine: 立ち上がりを遅らせ、後半で追いつくカーブ（Cセグメントの数値だけをこの進行度でずらす）
const easeInSine = (t: number): number => 1 - Math.cos((t * Math.PI) / 2);

// 辺（V・H）の両端点とその辺につながるCの端点が別々の進行度で動くと、
// 同じ直線の両端が違うタイミングで動くことになり辺が歪んで左右非対称に見える。
// そのため全頂点（M・V・C）を同じ tCurve に統一し、平坦→カーブが左右均等に遅れて開くようにする。
const interpolateSegs = (startSegs: Seg[], endSegs: Seg[], t: number): Seg[] => {
  const tCurve = easeInSine(t);
  return endSegs.map((seg, i) => {
    const s = startSegs[i];
    switch (seg.type) {
      case "M":
        return { type: "M", x: lerp((s as typeof seg).x, seg.x, tCurve), y: lerp((s as typeof seg).y, seg.y, tCurve) };
      case "H":
        return { type: "H", x: lerp((s as typeof seg).x, seg.x, tCurve) };
      case "V":
        return { type: "V", y: lerp((s as typeof seg).y, seg.y, tCurve) };
      case "C":
        return {
          type: "C",
          x1: lerp((s as typeof seg).x1, seg.x1, tCurve),
          y1: lerp((s as typeof seg).y1, seg.y1, tCurve),
          x2: lerp((s as typeof seg).x2, seg.x2, tCurve),
          y2: lerp((s as typeof seg).y2, seg.y2, tCurve),
          x: lerp((s as typeof seg).x, seg.x, tCurve),
          y: lerp((s as typeof seg).y, seg.y, tCurve),
        };
      default:
        return seg;
    }
  });
};

// objectBoundingBox（0〜1の比率）でパス文字列を組み立てる。レスポンシブに要素の実サイズが変わっても追従する。
const toPathString = (shape: Shape, segs: Seg[]): string =>
  segs
    .map((seg) => {
      switch (seg.type) {
        case "M":
          return `M${(seg.x / shape.width).toFixed(5)},${(seg.y / shape.height).toFixed(5)}`;
        case "H":
          return `H${(seg.x / shape.width).toFixed(5)}`;
        case "V":
          return `V${(seg.y / shape.height).toFixed(5)}`;
        case "C":
          return `C${(seg.x1 / shape.width).toFixed(5)},${(seg.y1 / shape.height).toFixed(5)} ${(seg.x2 / shape.width).toFixed(5)},${(seg.y2 / shape.height).toFixed(5)} ${(seg.x / shape.width).toFixed(5)},${(seg.y / shape.height).toFixed(5)}`;
        case "Z":
          return "Z";
      }
    })
    .join("");

// ローディング時に .p-top-mv-images のクリップ形状を、中央の直線から仕様の円弧カットへ開くアニメーション
export const mvClipOpen = (): gsap.core.Tween | null => {
  const path = document.querySelector<SVGPathElement>("#mv-clip-path");
  if (!path) return null;

  const shape = window.innerWidth < config.breakPoint ? SP_SHAPE : PC_SHAPE;
  const endSegs = buildEndSegs(shape);
  const startSegs = buildStartSegs(shape, endSegs);

  const proxy = { t: 0 };
  path.setAttribute("d", toPathString(shape, startSegs));

  return gsap.to(proxy, {
    t: 1,
    duration: 2.2,
    ease: CustomEase.create("custom", "M0,0 C0.093,0.455 0.121,0.928 1,1 "),
    delay: 0.5,
    onUpdate: () => {
      path.setAttribute("d", toPathString(shape, interpolateSegs(startSegs, endSegs, proxy.t)));
    },
  });
};
